import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import type { Video } from '../lib/types';
import { FilterPills } from '../components/FilterPills';
import { Thumb } from '../components/Thumb';

const TABS = ['視聴履歴', 'あとで見る'] as const;
type Tab = (typeof TABS)[number];

type HistoryRow = { videoId: string; positionSec: number };

export function History(): JSX.Element {
  const [tab, setTab] = useState<Tab>('視聴履歴');
  const [videos, setVideos] = useState<Video[]>([]);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [laterIds, setLaterIds] = useState<string[]>([]);
  const nav = useNavigate();

  useEffect(() => {
    api.listVideos().then(setVideos);
    api.history().then((rows) => setHistory(rows.map((r) => ({ videoId: r.videoId, positionSec: r.positionSec }))));
    api.listLaterVideoIds().then(setLaterIds).catch(() => setLaterIds([]));
  }, []);

  const byId = useMemo(() => new Map(videos.map((v) => [v.videoId, v])), [videos]);
  const positionById = useMemo(() => new Map(history.map((h) => [h.videoId, h.positionSec])), [history]);

  const list: Video[] = pickTab(tab, history, laterIds)
    .map((videoId) => byId.get(videoId))
    .filter(Boolean)
    .map((v) => withProgress(v!, positionById.get(v!.videoId))) as Video[];

  return (
    <div style={{ padding: '26px 30px 60px' }}>
      <h1 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 22, margin: '0 0 20px' }}>視聴履歴</h1>
      <div style={{ marginBottom: 16 }}>
        <FilterPills options={[...TABS]} value={tab} onChange={(v) => setTab(v as Tab)} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {list.length === 0 && (
          <div style={{ padding: '18px 4px', color: 'var(--ink-quiet)', fontSize: 13 }}>
            該当する動画がありません。
          </div>
        )}
        {list.map((v) => {
          const positionSec = positionById.get(v.videoId);
          return (
            <div
              key={v.videoId}
              onClick={() => nav(`/videos/${v.videoId}`)}
              style={{
                display: 'flex',
                gap: 14,
                alignItems: 'center',
                padding: '12px 14px',
                background: 'var(--surface)',
                border: '1px solid var(--border-soft)',
                borderRadius: 13,
                cursor: 'pointer',
              }}
            >
              <Thumb video={v} radius={9} showLabel={false} showDuration={false} style={{ width: 132, flex: 'none' }} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 4 }}>{v.title}</div>
                <div style={{ fontSize: 11.5, color: 'var(--ink-quiet)' }}>
                  {v.channelName} · {stateLabel(positionSec, v.durationSec)}
                </div>
              </div>
              <span style={{ fontSize: 12, color: 'var(--brand)', fontWeight: 700, flex: 'none' }}>
                {historyLink(positionSec, v.durationSec)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function pickTab(tab: Tab, history: HistoryRow[], laterIds: string[]): string[] {
  switch (tab) {
    case '視聴履歴':
      return history.map((h) => h.videoId);
    case 'あとで見る':
      return laterIds;
    default:
      return [];
  }
}

// 実データから Thumb の進捗バー用に progress: "N%" を注入する。
function withProgress(v: Video, positionSec: number | undefined): Video {
  if (!positionSec || !v.durationSec) return v;
  const pct = Math.min(100, Math.round((positionSec / v.durationSec) * 100));
  return { ...v, progress: `${pct}%` };
}

function historyLink(positionSec: number | undefined, durationSec: number | undefined): string {
  if (!positionSec) return '見る →';
  if (durationSec && positionSec >= durationSec - 5) return 'もう一度見る →';
  if (positionSec > 5) return '続きから見る →';
  return '見る →';
}

function stateLabel(positionSec: number | undefined, durationSec: number | undefined): string {
  if (!positionSec) return '未視聴';
  if (durationSec && positionSec >= durationSec - 5) return '視聴済み';
  if (durationSec) {
    const pct = Math.min(100, Math.round((positionSec / durationSec) * 100));
    return `${pct}% まで視聴`;
  }
  const m = Math.floor(positionSec / 60);
  const s = positionSec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')} まで視聴`;
}
